/**
 * Invoices: the ledger of documents, and the form that raises a new one.
 *
 * An invoice raised here is frequently NOT a record of something the game just
 * did — it is a document for something that happened last Tuesday, settled by a
 * method the operator picked, possibly already paid. So the form carries the
 * fields the automatic path has no need of: the payment method (plain text, from
 * the payment-logo set), the date and the time the document is dated, whether it
 * is already settled, the Shrooms it covers, and the number — pre-filled with the
 * next one off the shared counter and editable (the server refuses a duplicate).
 * A custom-amount purchase gets standard, editable texts.
 *
 * Issued invoices are edited from the list ("Edit"): number, status, type,
 * method, transaction id, amounts, currency, Shrooms, date and time, texts,
 * item code, recipient. Each save is audit-logged on the player's history. No
 * invoice action here ever changes a balance — the Shrooms are informational.
 *
 * Players created in the admin panel never get invoice e-mails; the form and the
 * list say so (the rule itself is enforced on the server).
 *
 * "Import transactions" opens the transaction import (transaction-import.ts) above the list.
 */
import { el, clear, toast } from '../../dom';
import {
  adminApi,
  shopApi,
  type AdminInvoice,
  type AdminInvoicePage,
  type InvoicePaymentMethodGroup,
  type InvoiceStatusValue,
  type Package,
  type RaiseInvoice,
  type UpdateInvoice,
} from '../../game-api';
import { importPanel } from './transaction-import';
import { combineLocalDateTime, fmtDateTime, invoiceFilename, localDateValue, localTimeValue } from './format';
import { CUSTOM_PURCHASE_NOTE, STANDARD_TEXTS, fillStandardText } from './invoice-texts';
import {
  EU_CURRENCIES, INVOICE_CURRENCY_GROUPS, INVOICE_STATUSES, ITEM_CODE_MANUAL, MASKED_PAN_HINT, MASKED_PAN_INPUT_MAX,
  MASKED_PAN_PLACEHOLDER, PAYMENT_DETAILS_HINT, PAYMENT_DETAILS_PLACEHOLDER, PAYMENT_METHOD_GROUPS, VAT_RATES,
  eurCentsForShrooms, exclFromIncl, inclFromExcl, shroomItemCode, shroomsFromEur, statusLabel, totalsLine,
} from './invoice-options';
import { playerPicker, type PickedPlayer } from './player-picker';
import {
  apiMessage, btn, emptyBox, errorBox, input, labelled, modal, panel, pill,
  saveBlob, spinner, table, textarea, toastError,
} from './ui';

const PAGE_SIZE = 20;

/**
 * A player handed over by the Players tab's "Invoice" button. The admin shell
 * switches to this section right after, and the form opens with them chosen.
 */
let pendingPlayer: PickedPlayer | null = null;
export function invoiceFor(p: PickedPlayer): void { pendingPlayer = p; }

const money = (cents: number, currency: string) => `${(cents / 100).toFixed(2)} ${currency}`;

export { EU_CURRENCIES, INVOICE_CURRENCY_GROUPS };

const SELECT = 'w-full rounded bg-surface border border-border px-2 py-1.5 text-sm';
const DATE_INPUT = 'w-full px-2.5 py-1.5 rounded-lg bg-surface-2 border border-border text-sm text-text outline-none focus:border-gold';

/**
 * The payment-method dropdown: plain text, visually separated by group with
 * <optgroup> (Card, Digital wallets, SEPA / bank, …). The option VALUE is the
 * text printed on the invoice. `current` is kept as an extra option when it is
 * not in the list — an older invoice may carry a method written before the list
 * existed, and editing it must never silently drop it.
 */
export function methodSelect(groups: InvoicePaymentMethodGroup[], current?: string | null): HTMLSelectElement {
  const sel = el('select', SELECT, {}, el('option', '', { value: '' }, '— none —')) as HTMLSelectElement;
  for (const g of groups) {
    sel.appendChild(el('optgroup', '', { label: g.label },
      ...g.methods.map((m) => el('option', '', { value: m }, m))));
  }
  if (current && !groups.some((g) => g.methods.includes(current))) {
    sel.appendChild(el('optgroup', '', { label: 'Saved on this invoice' }, el('option', '', { value: current }, current)));
  }
  sel.value = current ?? '';
  return sel;
}

/**
 * The currency dropdown, split into "EU currencies" and "Other currencies". A
 * currency stored on an older invoice that is no longer offered (BGN) is kept
 * as its own option, so editing that invoice does not change it.
 */
export function currencySelect(groups: Array<{ label: string; currencies: string[] }>, current = 'EUR'): HTMLSelectElement {
  const sel = el('select', SELECT, {}) as HTMLSelectElement;
  for (const g of groups) {
    sel.appendChild(el('optgroup', '', { label: g.label },
      ...g.currencies.map((c) => el('option', '', { value: c }, c))));
  }
  if (!groups.some((g) => g.currencies.includes(current))) {
    sel.appendChild(el('optgroup', '', { label: 'Saved on this invoice' }, el('option', '', { value: current }, current)));
  }
  sel.value = current;
  return sel;
}

/**
 * The status dropdown: PAID, PENDING, NOT PAID, CANCELLED, REFUNDED. Option
 * values are the stored spelling (the list's); a status stored on an older
 * invoice that is not in the list stays selectable.
 */
export function statusSelect(current = 'PAID'): HTMLSelectElement {
  const sel = el('select', SELECT, {},
    ...INVOICE_STATUSES.map((s) => el('option', '', { value: s.stored }, s.label))) as HTMLSelectElement;
  if (!INVOICE_STATUSES.some((s) => s.stored === current)) {
    sel.appendChild(el('optgroup', '', { label: 'Saved on this invoice' }, el('option', '', { value: current }, statusLabel(current))));
  }
  sel.value = current;
  return sel;
}

/** VAT rate dropdown (percent), plus the invoice's own rate when it is not listed. */
export function vatSelect(current = 0): HTMLSelectElement {
  const rates = VAT_RATES.includes(current) ? VAT_RATES : [...VAT_RATES, current].sort((a, b) => a - b);
  const sel = el('select', SELECT, {}, ...rates.map((r) => el('option', '', { value: String(r) }, `${r} %`))) as HTMLSelectElement;
  sel.value = String(current);
  return sel;
}

/** DEPOSIT (IN, money received) / WITHDRAWAL (OUT, money paid out). */
export function typeSelect(current: 'IN' | 'OUT' = 'IN'): HTMLSelectElement {
  const sel = el('select', SELECT, {},
    el('option', '', { value: 'IN' }, 'DEPOSIT — money received'),
    el('option', '', { value: 'OUT' }, 'WITHDRAWAL — money paid out')) as HTMLSelectElement;
  sel.value = current;
  return sel;
}

/** The standard-text picker ("Insert…"), filtered to the invoice type. */
export function fillInsert(sel: HTMLSelectElement, type: 'IN' | 'OUT'): void {
  clear(sel);
  sel.appendChild(el('option', '', { value: '' }, 'Insert…'));
  for (const t of STANDARD_TEXTS.filter((x) => x.types.includes(type))) {
    sel.appendChild(el('option', '', { value: t.key }, t.template.replace('{n}', 'N').replace('{cur}', 'currency')));
  }
  sel.value = '';
}

/** The text a Shroom amount fills in when no standard text was picked. */
function defaultTemplate(type: string): string {
  const key = type === 'OUT' ? 'payout' : 'premium';
  return STANDARD_TEXTS.find((t) => t.key === key)!.template;
}

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad'> = { PAID: 'ok', PENDING: 'warn' };

/** Card number input + hint (§8): masked only, room for spaces. */
export function cardNumberField(value = ''): { input: HTMLInputElement; wrap: HTMLElement } {
  const f = input(MASKED_PAN_PLACEHOLDER, value);
  f.maxLength = MASKED_PAN_INPUT_MAX;
  f.autocomplete = 'off';
  f.inputMode = 'text';
  return { input: f, wrap: el('div', '', {}, f, el('div', 'text-[10px] text-text-dim mt-0.5', {}, MASKED_PAN_HINT)) };
}

/** Payment details textarea + hint (§8): free text, printed as typed. */
export function paymentDetailsField(value = ''): { input: HTMLTextAreaElement; wrap: HTMLElement } {
  const f = textarea(PAYMENT_DETAILS_PLACEHOLDER, value, 3);
  return { input: f, wrap: el('div', '', {}, f, el('div', 'text-[10px] text-text-dim mt-0.5', {}, PAYMENT_DETAILS_HINT)) };
}

function dateTimeInputs(at: Date) {
  const date = el('input', DATE_INPUT, { type: 'date', value: localDateValue(at) }) as HTMLInputElement;
  const time = el('input', DATE_INPUT, { type: 'time', value: localTimeValue(at) }) as HTMLInputElement;
  return { date, time, iso: () => combineLocalDateTime(date.value, time.value) };
}


export function renderInvoices(host: HTMLElement): () => void {
  let cancelled = false;
  const state = { search: '', type: '', limit: PAGE_SIZE, offset: 0 };
  let methods: InvoicePaymentMethodGroup[] = PAYMENT_METHOD_GROUPS;
  let currencyGroups = INVOICE_CURRENCY_GROUPS;

  const search = input('Search by number, player or description…');
  const typeSel = el('select', 'rounded bg-surface border border-border px-2 py-1.5 text-sm', {},
    el('option', '', { value: '' }, 'All types'),
    el('option', '', { value: 'IN' }, 'IN — money received'),
    el('option', '', { value: 'OUT' }, 'OUT — money paid out')) as HTMLSelectElement;
  const listHost = el('div', 'min-h-[80px]');
  const pager = el('div', 'flex items-center justify-between gap-2 mt-3 flex-wrap');

  let timer: ReturnType<typeof setTimeout> | undefined;
  search.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(() => { state.search = search.value.trim(); state.offset = 0; void load(); }, 300);
  });
  typeSel.addEventListener('change', () => { state.type = typeSel.value; state.offset = 0; void load(); });

  async function load() {
    clear(listHost); clear(pager);
    listHost.appendChild(spinner());
    try {
      const page = await adminApi.invoices({ ...state, type: state.type || undefined });
      if (cancelled) return;
      draw(page);
    } catch (e) {
      if (cancelled) return;
      clear(listHost);
      listHost.appendChild(errorBox(apiMessage(e, "Couldn't load invoices."), () => void load()));
    }
  }

  function draw(page: AdminInvoicePage) {
    clear(listHost); clear(pager);
    if (!page.items.length) { listHost.appendChild(emptyBox('No invoices yet.')); return; }

    listHost.appendChild(table(
      ['Number', 'Dated', 'Player', 'Description', 'Method', 'Total', 'Status', ''],
      page.items.map((inv) => [
        el('div', 'min-w-0', {},
          el('span', 'font-mono text-xs', {}, inv.number),
          inv.maskedPan ? el('div', 'font-mono text-[10px] text-text-dim', { title: 'Card number' }, inv.maskedPan) : null,
          inv.emailSuppressed ? el('div', 'text-[10px] text-text-dim', { title: 'Admin-created player — invoices are never e-mailed' }, 'not e-mailed') : null),
        fmtDateTime(inv.issuedAt ?? inv.createdAt),
        el('div', 'min-w-0', {},
          el('div', 'text-sm truncate', {}, inv.recipientName ?? inv.user?.username ?? inv.user?.email ?? '—'),
          inv.recipientCompany ? el('div', 'text-[11px] text-text-dim truncate', {}, inv.recipientCompany) : null),
        el('span', 'text-sm', {}, inv.description),
        inv.paymentMethod ?? '—',
        el('span', 'font-mono text-xs', {}, money(inv.totalCents, inv.currency)),
        pill(statusLabel(inv.status), STATUS_TONE[inv.status] ?? 'bad'),
        el('div', 'flex gap-1.5', {},
          btn('PDF', () => void download(inv)),
          btn('Edit', () => openEdit(inv)),
          btn(inv.status === 'PAID' ? 'Unpay' : 'Mark paid', () => void setPaid(inv))),
      ])));

    const shown = `${page.offset + 1}–${Math.min(page.offset + page.limit, page.total)} of ${page.total}`;
    pager.appendChild(el('span', 'text-xs text-text-dim', {}, shown));
    pager.appendChild(el('div', 'flex gap-2', {},
      btn('Previous', () => { state.offset = Math.max(0, state.offset - state.limit); void load(); }),
      btn('Next', () => {
        if (state.offset + state.limit >= page.total) return;
        state.offset += state.limit;
        void load();
      })));
  }

  async function download(inv: AdminInvoice) {
    try {
      const blob = await adminApi.invoicePdf(inv.number);
      saveBlob(blob, invoiceFilename(inv.id, inv.number));
    } catch (e) { toastError(e, 'Could not download the invoice'); }
  }

  async function setPaid(inv: AdminInvoice) {
    try {
      await adminApi.setInvoicePaid(inv.id, inv.status !== 'PAID');
      toast(inv.status === 'PAID' ? 'Marked unpaid' : 'Marked paid');
      void load();
    } catch (e) { toastError(e, 'Could not change the status'); }
  }

  // ─── The form ──────────────────────────────────────────────────────────────
  function openForm(preset?: PickedPlayer) {
    const type = typeSelect('IN');
    const method = methodSelect(methods);
    const transactionId = input('Payment reference (optional)');
    const card = cardNumberField();
    const payDetails = paymentDetailsField();
    const status = statusSelect('PAID');
    const currency = currencySelect(currencyGroups, 'EUR');
    const number = input('Invoice number');
    const numberNote = el('div', 'text-[11px] text-text-dim min-h-[14px]');
    let suggested = '';
    const issued = dateTimeInputs(new Date());
    const now = btn('Now', () => {
      const at = new Date();
      issued.date.value = localDateValue(at);
      issued.time.value = localTimeValue(at);
    });
    const firstName = input('First name');
    const lastName = input('Last name');
    const recipientCompany = input('Company (optional)');
    const recipientCode = input('Personal / reg. code (optional)');
    const recipientAddress = textarea('Street, city, postcode, country', '', 3);
    const prefillNote = el('div', 'text-[11px] text-text-dim min-h-[14px]');
    const pack = el('select', SELECT, {}, el('option', '', { value: 'custom' }, 'CUSTOM')) as HTMLSelectElement;
    const excl = input('0.00', '', 'number');
    excl.min = '0.01'; excl.step = '0.01';
    const incl = input('0.00', '', 'number');
    incl.min = '0.01'; incl.step = '0.01';
    const vatRate = vatSelect(0);
    const shrooms = input('e.g. 2500', '', 'number');
    shrooms.min = '0'; shrooms.step = '1';
    const shroomsNote = el('div', 'text-[11px] text-text-dim min-h-[14px]');
    const insert = el('select', SELECT, {}) as HTMLSelectElement;
    const description = input('What is being billed');
    const itemCode = input(ITEM_CODE_MANUAL, ITEM_CODE_MANUAL);
    const totals = el('div', 'text-xs font-mono text-[#ffd97a] min-h-[16px]');
    const note = textarea('Note printed on the document (optional)', '', 3);

    let packages: Package[] = [];
    let shroomsPerEur = 100;

    // Auto-filled texts follow the amounts until the operator types their own.
    let descTouched = false;
    let codeTouched = false;
    let noteTouched = false;
    let shroomsTouched = false;
    let chosenTemplate: string | null = null;
    description.addEventListener('input', () => { descTouched = true; });
    itemCode.addEventListener('input', () => { codeTouched = true; });
    note.addEventListener('input', () => { noteTouched = true; });

    const shroomsNum = () => { const n = Math.floor(Number(shrooms.value)); return Number.isFinite(n) && n > 0 ? n : 0; };
    const centsOf = (v: string) => { const c = Math.round(Number(v) * 100); return Number.isFinite(c) && c > 0 ? c : 0; };
    const rate = () => Number(vatRate.value) || 0;
    const selectedPack = () => packages.find((p) => p.id === pack.value) ?? null;

    const refreshInsert = () => fillInsert(insert, type.value as 'IN' | 'OUT');
    const refreshTotals = () => {
      const c = centsOf(excl.value);
      totals.textContent = c ? totalsLine(c, rate(), currency.value) : '';
      shroomsNote.textContent = currency.value === 'EUR'
        ? `${shroomsPerEur} Shrooms per EUR. Informational — never credited to the player.`
        : 'Type the Shrooms for a non-EUR invoice (no exchange rate is used). Never credited.';
    };
    const autoTexts = () => {
      const n = shroomsNum();
      const p = selectedPack();
      if (!descTouched) {
        if (p) description.value = `${p.shrooms.toLocaleString('en-GB')} Shrooms — ${p.name}`;
        else if (chosenTemplate) description.value = fillStandardText(chosenTemplate, n, currency.value);
        else if (n > 0) description.value = fillStandardText(defaultTemplate(type.value), n, currency.value);
      }
      if (!codeTouched) itemCode.value = n > 0 ? shroomItemCode(n) : chosenTemplate ? shroomItemCode(0) : ITEM_CODE_MANUAL;
      // With a Shroom count the PDF prints its own Shroom block; without one the
      // standard closing note stands in for it.
      if (!noteTouched) note.value = n > 0 ? '' : CUSTOM_PURCHASE_NOTE;
    };
    /** EUR only: Shrooms follow the money until typed by hand. */
    const shroomsFromMoney = () => {
      if (shroomsTouched || currency.value !== 'EUR') return;
      const c = centsOf(incl.value);
      shrooms.value = c ? String(shroomsFromEur(c, shroomsPerEur)) : '';
    };

    excl.addEventListener('input', () => {
      const c = centsOf(excl.value);
      incl.value = c ? (inclFromExcl(c, rate()) / 100).toFixed(2) : '';
      pack.value = 'custom';
      shroomsFromMoney(); autoTexts(); refreshTotals();
    });
    incl.addEventListener('input', () => {
      const c = centsOf(incl.value);
      excl.value = c ? (exclFromIncl(c, rate()) / 100).toFixed(2) : '';
      pack.value = 'custom';
      shroomsFromMoney(); autoTexts(); refreshTotals();
    });
    vatRate.addEventListener('change', () => {
      const c = centsOf(excl.value);
      if (c) incl.value = (inclFromExcl(c, rate()) / 100).toFixed(2);
      refreshTotals();
    });
    shrooms.addEventListener('input', () => {
      shroomsTouched = shrooms.value !== '';
      pack.value = 'custom';
      const n = shroomsNum();
      // EUR: the price follows from the Shrooms. Any other currency: the operator
      // types the price — no live rate is ever used.
      if (n > 0 && currency.value === 'EUR') {
        const c = eurCentsForShrooms(n, shroomsPerEur);
        incl.value = (c / 100).toFixed(2);
        excl.value = (exclFromIncl(c, rate()) / 100).toFixed(2);
      }
      autoTexts(); refreshTotals();
    });
    pack.addEventListener('change', () => {
      const p = selectedPack();
      descTouched = false; codeTouched = false;
      if (!p) { chosenTemplate = null; autoTexts(); refreshTotals(); return; }
      // Package prices are euro prices, incl. VAT.
      currency.value = 'EUR';
      const c = Math.round(p.priceEur * 100);
      incl.value = (c / 100).toFixed(2);
      excl.value = (exclFromIncl(c, rate()) / 100).toFixed(2);
      shrooms.value = String(p.shrooms);
      shroomsTouched = true;
      autoTexts(); refreshTotals();
    });
    currency.addEventListener('change', () => { if (selectedPack()) pack.value = 'custom'; autoTexts(); refreshTotals(); });
    type.addEventListener('change', refreshInsert);
    insert.addEventListener('change', () => {
      const t = STANDARD_TEXTS.find((x) => x.key === insert.value);
      insert.value = '';
      if (!t) return;
      chosenTemplate = t.template;
      descTouched = false; codeTouched = false;
      pack.value = 'custom';
      autoTexts();
    });
    refreshInsert();
    autoTexts();
    refreshTotals();

    adminApi.invoiceFormOptions().then((o) => {
      suggested = o.nextNumber;
      number.placeholder = `Auto — next ${o.nextNumber}`;
      if (!number.value) number.value = suggested;
      numberNote.textContent = 'Next number from the counter. Keep it, or type your own: 3-30 characters, letters, digits, - _ / . (must be unused).';
    }).catch(() => { numberNote.textContent = 'Leave empty for the next number from the counter.'; });

    // WITHDRAWAL is offered only while withdrawals are open; the Shroom price
    // comes from the same settings.
    adminApi.settings().then((st) => {
      if (st.cryptePerEur > 0) shroomsPerEur = st.cryptePerEur;
      const out = type.querySelector<HTMLOptionElement>('option[value="OUT"]');
      if (out && !st.withdrawalEnabled) {
        out.disabled = true;
        out.textContent = 'WITHDRAWAL — withdrawals are off';
        if (type.value === 'OUT') { type.value = 'IN'; refreshInsert(); }
      }
      refreshTotals();
    }).catch(() => {});

    shopApi.packages().then((list) => {
      packages = list;
      for (const p of list) {
        pack.appendChild(el('option', '', { value: p.id },
          `${p.name} — ${p.shrooms.toLocaleString('en-GB')} Shrooms · ${p.priceEur.toFixed(2)} EUR`));
      }
    }).catch(() => {});

    /**
     * Choosing a player fills in who the document is made out to.
     *
     * The whole point of raising an invoice by hand is a payment that never went
     * through a provider — so there is no charge record carrying a name or an
     * address, and the player's own details are the only source. Anything the
     * operator has already typed is left alone.
     */
    const player = playerPicker((p) => {
      prefillNote.textContent = '';
      adminApi.invoiceBillingDefaults(p.id).then((d) => {
        if (player.value() !== p.id) return;      // they picked someone else
        if (!firstName.value.trim() && !lastName.value.trim()) {
          const split = d.firstName || d.lastName;
          firstName.value = split ? (d.firstName ?? '') : d.recipientName;
          lastName.value = split ? (d.lastName ?? '') : '';
        }
        if (!recipientAddress.value.trim()) recipientAddress.value = d.recipientAddress;
        prefillNote.textContent = (d.hasDetails
          ? `Filled in from ${d.username ?? d.email}.`
          : `${d.username ?? d.email} — no name or address on file yet; type them here.`)
          + (d.emailSuppressed ? ' Admin-created player (admin-created, no email): this invoice will NOT be e-mailed.' : '');
      }).catch((e) => { prefillNote.textContent = apiMessage(e, "Couldn't load the player's billing details."); });
    }, { allowCreate: true });

    const save = btn('Generate invoice', async () => {
      const cents = centsOf(excl.value);
      const userId = player.value();
      if (!userId) { toast('Choose a player from the search, or create one', 'error'); return; }
      if (!description.value.trim()) { toast('A description is required', 'error'); return; }
      if (!cents) { toast('Enter the amount excl. VAT', 'error'); return; }
      const sh = shrooms.value === '' ? undefined : Number(shrooms.value);
      if (sh !== undefined && (!Number.isInteger(sh) || sh < 0)) { toast('Shrooms must be a whole number', 'error'); return; }
      // Blank date = now.
      const issuedAt = issued.date.value ? issued.iso() : new Date().toISOString();
      if (!issuedAt) { toast('Pick the issue date', 'error'); return; }
      const typedNumber = number.value.trim();

      const body: RaiseInvoice = {
        userId,
        type: type.value as 'IN' | 'OUT',
        amountCents: cents,
        description: description.value.trim(),
        itemCode: itemCode.value.trim() || ITEM_CODE_MANUAL,
        currency: currency.value,
        vatRate: rate(),
        paymentMethod: method.value || undefined,
        transactionId: transactionId.value.trim() || undefined,
        maskedPan: card.input.value.trim() || undefined,
        paymentDetails: payDetails.input.value.trim() || undefined,
        status: status.value.toLowerCase() as InvoiceStatusValue,
        recipientName: firstName.value.trim() || undefined,
        recipientSurname: lastName.value.trim() || undefined,
        recipientCompany: recipientCompany.value.trim() || undefined,
        recipientCode: recipientCode.value.trim() || undefined,
        recipientAddress: recipientAddress.value.trim() || undefined,
        issuedAt,
        note: note.value.trim() || undefined,
        // The untouched suggestion is NOT sent: the counter allocates it at save
        // time, so a deposit settling in between cannot make this a duplicate.
        number: typedNumber && typedNumber !== suggested ? typedNumber : undefined,
        shrooms: sh,
      };
      try {
        const created = await adminApi.raiseInvoice(body);
        toast(`Invoice ${created.number} created${created.emailSuppressed ? ' — not emailed (admin-created player)' : ''}`);
        close();
        state.offset = 0;
        void load();
      } catch (e) { toastError(e, 'Could not generate the invoice'); }
    }, 'primary');

    const g = () => el('div', 'grid gap-3', { style: 'grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));' });
    const section = (title: string) => el('div', 'text-[11px] uppercase tracking-widest text-[#ffd97a] mt-1', {}, title);
    const paymentGrid = g();
    paymentGrid.append(
      labelled('Payment method', method),
      labelled('Transaction ID', transactionId),
      labelled('Card number', card.wrap),
      labelled('Status', status));
    const amountGrid = g();
    amountGrid.append(
      labelled('Package', pack),
      labelled('Amount excl. VAT', excl),
      labelled('Amount incl. VAT', incl),
      labelled('VAT rate', vatRate),
      labelled('Shrooms (premium currency)', shrooms));
    const docGrid = g();
    docGrid.append(
      labelled('Currency', currency),
      labelled('Invoice number', number),
      labelled('Issue date', issued.date),
      labelled('Issue time', el('div', 'flex gap-1.5', {}, issued.time, now)));
    const recipientGrid = g();
    recipientGrid.append(
      labelled('First name', firstName),
      labelled('Last name', lastName),
      labelled('Personal / reg. code', recipientCode),
      labelled('Company', recipientCompany));

    const close = modal('Generate invoice',
      el('div', 'flex flex-col gap-3', {},
        labelled('User', player.wrap),
        labelled('Type', type),
        section('Payment'),
        paymentGrid,
        labelled('Payment details', payDetails.wrap),
        section('Document'),
        docGrid,
        numberNote,
        section('Recipient — leave blank to use the user account info'),
        recipientGrid,
        labelled('Billing address', recipientAddress),
        prefillNote,
        section('Amount'),
        amountGrid,
        shroomsNote,
        labelled('Standard text', insert),
        labelled('Description', description),
        labelled('Item code', itemCode),
        totals,
        labelled('Note', note)),
      [btn('Cancel', () => close()), save]);
    if (preset) player.set(preset);
  }

  // ─── Edit an issued invoice ────────────────────────────────────────────────
  function openEdit(inv: AdminInvoice) {
    const number = input('Invoice number', inv.number);
    const status = statusSelect(inv.status);
    const type = typeSelect(inv.type);
    const method = methodSelect(methods, inv.paymentMethod);
    const transactionId = input('Payment reference', inv.transactionId ?? '');
    const card = cardNumberField(inv.maskedPan ?? '');
    const payDetails = paymentDetailsField(inv.paymentDetails ?? '');
    const amount = input('0.00', (inv.amountCents / 100).toFixed(2), 'number');
    amount.min = '0.01'; amount.step = '0.01';
    const vatRate = vatSelect(inv.vatRate);
    const currency = currencySelect(currencyGroups, inv.currency);
    const shrooms = input('none', inv.shrooms == null ? '' : String(inv.shrooms), 'number');
    shrooms.min = '0'; shrooms.step = '1';
    const issued = dateTimeInputs(new Date(inv.issuedAt ?? inv.createdAt));
    const insert = el('select', SELECT, {}) as HTMLSelectElement;
    fillInsert(insert, inv.type);
    type.addEventListener('change', () => fillInsert(insert, type.value as 'IN' | 'OUT'));
    const description = input('What is being billed', inv.description);
    insert.addEventListener('change', () => {
      const t = STANDARD_TEXTS.find((x) => x.key === insert.value);
      insert.value = '';
      if (t) description.value = fillStandardText(t.template, Math.floor(Number(shrooms.value)) || 0, currency.value);
    });
    const itemCode = input(ITEM_CODE_MANUAL, inv.itemCode ?? '');
    const note = textarea('Note printed on the document', inv.note ?? '', 3);
    const recipientName = input('First name (older invoices: the full name)', inv.recipientName ?? '');
    const recipientSurname = input('Last name', inv.recipientSurname ?? '');
    const recipientCompany = input('Company', inv.recipientCompany ?? '');
    const recipientCode = input('Personal / reg. code', inv.recipientCode ?? '');
    const recipientAddress = textarea('Street, city, postcode, country', inv.recipientAddress ?? '', 3);

    const save = btn('Save changes', async () => {
      const patch: UpdateInvoice = {};
      const cents = Math.round(Number(amount.value) * 100);
      if (!Number.isFinite(cents) || cents < 1) { toast('Enter an amount above zero', 'error'); return; }
      const sh = shrooms.value === '' ? null : Number(shrooms.value);
      if (sh !== null && (!Number.isInteger(sh) || sh < 0)) { toast('Shrooms must be a whole number', 'error'); return; }
      const issuedAt = issued.iso();
      if (!issuedAt) { toast('Pick the issue date', 'error'); return; }
      if (!description.value.trim()) { toast('A description is required', 'error'); return; }

      if (number.value.trim() !== inv.number) patch.number = number.value.trim();
      if (status.value !== inv.status) patch.status = status.value.toLowerCase();
      if (type.value !== inv.type) patch.type = type.value as 'IN' | 'OUT';
      if ((method.value || null) !== inv.paymentMethod) patch.paymentMethod = method.value || null;
      if (cents !== inv.amountCents) patch.amountCents = cents;
      if ((Number(vatRate.value) || 0) !== inv.vatRate) patch.vatRate = Number(vatRate.value) || 0;
      if (currency.value !== inv.currency) patch.currency = currency.value;
      if (sh !== inv.shrooms) patch.shrooms = sh;
      // Minute resolution, like the pickers: an untouched date is not re-sent.
      const before = Math.floor(new Date(inv.issuedAt ?? inv.createdAt).getTime() / 60_000);
      if (Math.floor(new Date(issuedAt).getTime() / 60_000) !== before) patch.issuedAt = issuedAt;
      if (description.value.trim() !== inv.description) patch.description = description.value.trim();
      if ((note.value.trim() || null) !== inv.note) patch.note = note.value.trim() || null;
      const texts = [
        ['recipientName', recipientName], ['recipientSurname', recipientSurname],
        ['recipientCompany', recipientCompany], ['recipientCode', recipientCode],
        ['recipientAddress', recipientAddress], ['transactionId', transactionId], ['itemCode', itemCode],
        ['maskedPan', card.input], ['paymentDetails', payDetails.input],
      ] as const;
      for (const [key, field] of texts) {
        if ((field.value.trim() || null) !== (inv[key] ?? null)) patch[key] = field.value.trim() || null;
      }
      if (!Object.keys(patch).length) { toast('Nothing changed'); return; }
      try {
        const updated = await adminApi.updateInvoice(inv.id, patch);
        toast(`Invoice ${updated.number} saved`);
        close();
        void load();
      } catch (e) { toastError(e, 'Could not save the invoice'); }
    }, 'primary');

    const who = inv.user ? [inv.user.username, inv.user.email].filter(Boolean).join(' · ') : '—';
    const close = modal(`Edit invoice ${inv.number}`,
      el('div', 'flex flex-col gap-3', {},
        el('div', 'text-xs text-text-dim', {}, who),
        el('div', 'grid gap-3', { style: 'grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));' },
          labelled('Invoice number', number),
          labelled('Issue date', issued.date),
          labelled('Issue time', issued.time),
          labelled('Type', type),
          labelled('Status', status),
          labelled('Payment method', method),
          labelled('Transaction ID', transactionId),
          labelled('Card number', card.wrap),
          labelled('Currency', currency),
          labelled('Amount excl. VAT', amount),
          labelled('VAT rate', vatRate),
          labelled('Shrooms (premium currency)', shrooms),
          labelled('Item code', itemCode),
          labelled('First name', recipientName),
          labelled('Last name', recipientSurname),
          labelled('Personal / reg. code', recipientCode),
          labelled('Company', recipientCompany)),
        // Wide modal: the free-text blocks sit two to a row from tablet width up.
        el('div', 'grid gap-3 sm:grid-cols-2', {},
          labelled('Billing address', recipientAddress),
          labelled('Payment details', payDetails.wrap)),
        el('div', 'grid gap-3 sm:grid-cols-2', {},
          labelled('Standard text', insert),
          labelled('Description', description)),
        labelled('Note', note),
        el('div', 'text-[11px] text-text-dim', {},
          'Editing the Shrooms amount or status only changes the invoice — the player\'s balance is not touched. '
          + 'VAT and the total are recomputed from the amount and the rate. Every change is recorded on the player\'s history.')),
      [btn('Cancel', () => close()), save], undefined, { size: 'wide' });
  }

  // Import transactions: opens above the list; an import reloads it from the first page.
  const importHost = el('div');
  const toggleImport = () => {
    if (importHost.firstChild) { clear(importHost); return; }
    importHost.appendChild(importPanel(() => { state.offset = 0; void load(); }, () => clear(importHost)));
  };

  host.appendChild(panel('Invoices',
    el('div', 'flex flex-wrap gap-2 items-center mb-3', {},
      el('div', 'flex-1 min-w-[180px]', {}, search),
      typeSel,
      btn('Import transactions', toggleImport),
      btn('New invoice', () => openForm(), 'primary')),
    importHost,
    listHost,
    pager));

  // The method list feeds the form's dropdown; a failure just leaves it empty
  // (an invoice does not require a payment method).
  // A player sent over from the Players tab: open the form once the payment
  // methods are in, so its dropdown is not empty.
  const handed = pendingPlayer;
  pendingPlayer = null;
  adminApi.invoiceFormOptions().then((o) => {
    if (cancelled) return;
    if (o.paymentMethods?.length) methods = o.paymentMethods;
    if (o.currencyGroups?.length) currencyGroups = o.currencyGroups;
  }).catch(() => {})
    .finally(() => { if (handed && !cancelled) openForm(handed); });
  void load();

  return () => { cancelled = true; };
}
